import {
  AlertTriangle,
  ExternalLink,
  Package,
  Search,
  Wheat,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { DateRangePicker } from "@/components/shared/date-range-picker";
import { PageHeader } from "@/components/shared/page-header";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMe } from "@/hooks/use-auth";
import { useGrossProfitReport } from "@/hooks/use-gross-profit-report";
import { useRiders } from "@/hooks/use-riders";
import { useWorkspaces } from "@/hooks/use-workspaces";

import { GrossProfitChart } from "./gross-profit-chart";
import { GROSS_PROFIT_SOURCE_LABELS } from "./gross-profit-labels";
import {
  formatDateLabel,
  formatPercent,
  formatRupiah,
} from "./gross-profit-utils";

import type { GrossProfitSourceFilter } from "@artisancode/api-types";

export function GrossProfitPage() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [source, setSource] = useState<GrossProfitSourceFilter>("all");
  const [riderId, setRiderId] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = (workspacesData?.items ?? []).filter((w) => w.isActive);

  const { data: ridersData } = useRiders({ per_page: 100 });
  const riders = ridersData?.items ?? [];

  const { data, isLoading } = useGrossProfitReport({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    source: source === "all" ? undefined : source,
    rider_id: source !== "sale" && riderId ? riderId : undefined,
    workspace_id: workspaceId || undefined,
  });

  const summary = data?.summary ?? {
    revenue: 0,
    knownCostRevenue: 0,
    unknownCostRevenue: 0,
    cogsTotal: 0,
    grossProfit: 0,
    margin: null,
    operatingExpenses: null,
    riderPayrollExpenses: null,
    netProfit: null,
    totalTransactionsCount: 0,
    unknownTransactionsCount: 0,
    unknownItemsCount: 0,
  };

  const byProduct = data?.byProduct ?? [];
  const daily = data?.daily ?? [];

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return byProduct;
    const q = searchQuery.toLowerCase();
    return byProduct.filter(
      (p) =>
        p.productName.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q),
    );
  }, [byProduct, searchQuery]);

  const hasUnknownCogs =
    summary.unknownTransactionsCount > 0 || summary.unknownCostRevenue > 0;

  return (
    <TooltipProvider>
      <div>
        <PageHeader
          title="Laba Kotor"
          description="Laporan analisis margin dan laba kotor dari Penjualan Toko dan Penjualan Rider berdasarkan Harga Pokok Penjualan (HPP)."
        />

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {me?.canSwitchWorkspace && (
              <div className="w-48">
                <SearchableSelect
                  value={workspaceId}
                  onChange={setWorkspaceId}
                  options={workspaces.map((w) => ({
                    value: w.id,
                    label: w.name,
                  }))}
                  placeholder="Semua Workspace"
                />
              </div>
            )}
            <DateRangePicker
              from={dateFrom}
              to={dateTo}
              onChange={(f, t) => {
                setDateFrom(f);
                setDateTo(t);
              }}
            />
            <Select
              value={source}
              onValueChange={(v) => {
                const next = v as GrossProfitSourceFilter;
                setSource(next);
                if (next === "sale") setRiderId("");
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(GROSS_PROFIT_SOURCE_LABELS).map(
                  ([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
            {source !== "sale" && (
              <div className="w-48">
                <SearchableSelect
                  value={riderId}
                  onChange={setRiderId}
                  options={[
                    { value: "", label: "Semua rider" },
                    ...riders.map((r) => ({ value: r.id, label: r.name })),
                  ]}
                  placeholder="Semua rider"
                  clearable
                />
              </div>
            )}
          </div>
        </div>

        {/* Warning Banner for Unknown HPP */}
        {hasUnknownCogs && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="flex-1 text-sm">
                <p className="font-semibold">
                  {summary.unknownTransactionsCount} transaksi (
                  {formatRupiah(summary.unknownCostRevenue)}) belum memiliki HPP
                  lengkap.
                </p>
                <p className="mt-1 text-amber-800 dark:text-amber-300/90">
                  Margin dan laba kotor dihitung tanpa transaksi ini. Lengkapi
                  harga pokok produk atau harga beli bahan baku agar perhitungan
                  laba kotor mencakup seluruh penjualan.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="h-8 border-amber-300 bg-white/80 text-xs hover:bg-white dark:border-amber-700 dark:bg-amber-900/60 dark:hover:bg-amber-900"
                  >
                    <Link
                      to="/master/products"
                      className="flex items-center gap-1.5"
                    >
                      <Package className="h-3.5 w-3.5" />
                      Lengkapi HPP Produk
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="h-8 border-amber-300 bg-white/80 text-xs hover:bg-white dark:border-amber-700 dark:bg-amber-900/60 dark:hover:bg-amber-900"
                  >
                    <Link
                      to="/master/raw-materials"
                      className="flex items-center gap-1.5"
                    >
                      <Wheat className="h-3.5 w-3.5" />
                      Lengkapi Harga Bahan Baku
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Omzet
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : formatRupiah(summary.revenue)}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatRupiah(summary.knownCostRevenue)} HPP tercatat
                {summary.unknownCostRevenue > 0 &&
                  ` · ${formatRupiah(summary.unknownCostRevenue)} tanpa HPP`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total HPP (COGS)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? "..." : formatRupiah(summary.cogsTotal)}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Beban pokok omzet terdata
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Laba Kotor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${
                  summary.grossProfit < 0 ? "text-destructive" : ""
                }`}
              >
                {isLoading ? "..." : formatRupiah(summary.grossProfit)}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Omzet HPP lengkap - Total HPP
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Margin Laba Kotor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <span
                  className={`text-2xl font-bold ${
                    summary.margin === null
                      ? ""
                      : summary.margin < 0
                        ? "text-destructive"
                        : summary.margin >= 0.4
                          ? "text-emerald-600 dark:text-emerald-400"
                          : ""
                  }`}
                >
                  {isLoading ? "..." : formatPercent(summary.margin)}
                </span>
                {!isLoading && summary.margin !== null && (
                  <Badge
                    variant={
                      summary.margin >= 0.4
                        ? "default"
                        : summary.margin >= 0.2
                          ? "secondary"
                          : "destructive"
                    }
                    className="text-[10px]"
                  >
                    {summary.margin >= 0.4
                      ? "Sehat"
                      : summary.margin >= 0.2
                        ? "Sedang"
                        : "Rendah"}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {summary.knownCostRevenue > 0
                  ? "Dari omzet dengan HPP lengkap"
                  : "Belum ada data HPP"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Net Profit — only for the unfiltered report; expenses can't be split per channel/rider */}
        {!isLoading && summary.netProfit !== null ? (
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Biaya Operasional
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatRupiah(summary.operatingExpenses ?? 0)}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pengeluaran selain bahan baku, kemasan & gaji rider
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Gaji & Bonus Rider
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatRupiah(summary.riderPayrollExpenses ?? 0)}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pengeluaran kategori gaji & bonus rider
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Laba Bersih
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    summary.netProfit < 0 ? "text-destructive" : ""
                  }`}
                >
                  {formatRupiah(summary.netProfit)}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Laba kotor - biaya operasional - gaji rider
                  {hasUnknownCogs && " (tanpa omzet yang belum ada HPP)"}
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          !isLoading && (
            <p className="mb-6 text-xs text-muted-foreground">
              Laba bersih hanya tampil untuk &quot;Semua Sumber&quot; tanpa
              filter rider, karena pengeluaran tidak bisa dipisah per sumber.
            </p>
          )
        )}

        {/* Daily Chart */}
        {!isLoading && daily.length > 0 && <GrossProfitChart data={daily} />}

        {/* Daily Breakdown Table */}
        {!isLoading && daily.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">
                Rincian Harian
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tanggal</TableHead>
                      <TableHead className="text-right">Total Omzet</TableHead>
                      <TableHead className="text-right">
                        Omzet HPP Ada
                      </TableHead>
                      <TableHead className="text-right">Total HPP</TableHead>
                      <TableHead className="text-right">Laba Kotor</TableHead>
                      <TableHead className="text-right">Margin</TableHead>
                      <TableHead className="text-center">Tanpa HPP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {daily.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell className="font-medium">
                          {formatDateLabel(day.date)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatRupiah(day.revenue)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatRupiah(day.knownCostRevenue)}
                        </TableCell>
                        <TableCell className="text-right text-amber-600 dark:text-amber-400">
                          {formatRupiah(day.cogsTotal)}
                        </TableCell>
                        <TableCell
                          className={`text-right font-medium ${
                            day.grossProfit < 0
                              ? "text-destructive"
                              : "text-emerald-600 dark:text-emerald-400"
                          }`}
                        >
                          {formatRupiah(day.grossProfit)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatPercent(day.margin)}
                        </TableCell>
                        <TableCell className="text-center">
                          {day.unknownCount > 0 ? (
                            <Badge
                              variant="secondary"
                              className="text-amber-600"
                            >
                              {day.unknownCount} tx
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* By-Product Table */}
        <Card className="mb-6">
          <CardHeader className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base font-semibold">
              Rincian Per Produk ({filteredProducts.length})
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari produk / kategori..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produk</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead className="text-right">Qty Terjual</TableHead>
                    <TableHead className="text-right">Total Omzet</TableHead>
                    <TableHead className="text-right">HPP / Unit</TableHead>
                    <TableHead className="text-right">Total HPP</TableHead>
                    <TableHead className="text-right">Laba Kotor</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="text-center">Status HPP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="h-24 text-center">
                        Memuat data laporan laba kotor...
                      </TableCell>
                    </TableRow>
                  ) : filteredProducts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        className="h-24 text-center text-muted-foreground"
                      >
                        Tidak ada transaksi penjualan pada filter yang dipilih.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProducts.map((p) => (
                      <TableRow key={p.productId}>
                        <TableCell className="font-medium">
                          {p.productName}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {p.category || "Umum"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {p.qty.toLocaleString("id-ID")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div>{formatRupiah(p.revenue)}</div>
                          {p.unknownCostRevenue > 0 &&
                            p.knownCostRevenue > 0 && (
                              <div className="text-[11px] text-muted-foreground">
                                HPP ada: {formatRupiah(p.knownCostRevenue)}
                              </div>
                            )}
                          {p.unknownCostRevenue > 0 && (
                            <div className="text-[11px] text-amber-600 dark:text-amber-400">
                              Belum ada HPP:{" "}
                              {formatRupiah(p.unknownCostRevenue)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {p.cogsPerUnit !== null
                            ? formatRupiah(p.cogsPerUnit)
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right text-amber-600 dark:text-amber-400">
                          {p.cogsTotal !== null
                            ? formatRupiah(p.cogsTotal)
                            : "-"}
                        </TableCell>
                        <TableCell
                          className={`text-right font-medium ${
                            p.grossProfit === null
                              ? "text-muted-foreground"
                              : p.grossProfit < 0
                                ? "text-destructive"
                                : "text-emerald-600 dark:text-emerald-400"
                          }`}
                        >
                          {p.grossProfit !== null
                            ? formatRupiah(p.grossProfit)
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatPercent(p.margin)}
                        </TableCell>
                        <TableCell className="text-center">
                          {p.cogsKnown && p.unknownCostRevenue === 0 ? (
                            <Badge
                              variant="outline"
                              className="border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300"
                            >
                              Lengkap
                            </Badge>
                          ) : p.knownCostRevenue > 0 &&
                            p.unknownCostRevenue > 0 ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="cursor-pointer border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300"
                                >
                                  Sebagian
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Sebagian omzet (
                                  {formatRupiah(p.unknownCostRevenue)}) belum
                                  memiliki HPP
                                </p>
                                {p.missingCostItems &&
                                  p.missingCostItems.length > 0 && (
                                    <p>
                                      Bahan tanpa harga:{" "}
                                      {p.missingCostItems.join(", ")}
                                    </p>
                                  )}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="destructive"
                                  className="cursor-pointer"
                                >
                                  Belum Ada
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                {p.missingCostItems &&
                                p.missingCostItems.length > 0 ? (
                                  <p>
                                    Bahan tanpa harga:{" "}
                                    {p.missingCostItems.join(", ")}
                                  </p>
                                ) : (
                                  <p>
                                    HPP produk bernilai Rp0 atau belum diset
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
